import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { searchResultItemSchema } from "@/components/layout/Navbar/ApplicationSearch/application-search.schema";
import { SearchResultItem } from "@/components/layout/Navbar/ApplicationSearch/application-search.types";
import { userApi } from "@/store/services/user.service";

export interface RecentSearchState {
    recentSearches: SearchResultItem[];
}

const initialState: RecentSearchState = { recentSearches: [] };

const getStorageKey = (userId: string) => `lssa:searches:${userId}`;

const updateLocalStorage = (userId: string, recentSearches: SearchResultItem[]) => {
    const storageKey = getStorageKey(userId);
    localStorage.setItem(storageKey, JSON.stringify(recentSearches));
};

const MAX_RECENT_SEARCHES = 10;

const recentSearchSlice = createSlice({
    name: "recentSearch",
    initialState,
    reducers: {
        addRecentSearch: (state, action: PayloadAction<{ userId: string; recentSearch: SearchResultItem }>) => {
            const { userId, recentSearch } = action.payload;

            state.recentSearches = state.recentSearches.filter(
                (s) => !(s.type === recentSearch.type && s.sortValue === recentSearch.sortValue),
            );

            state.recentSearches = [recentSearch, ...state.recentSearches];

            if (state.recentSearches.length > MAX_RECENT_SEARCHES) {
                state.recentSearches = state.recentSearches.slice(0, MAX_RECENT_SEARCHES);
            }

            updateLocalStorage(userId, state.recentSearches);
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(userApi.endpoints.getUser.matchFulfilled, (state, { payload: user }) => {
            try {
                const recentSearches = localStorage.getItem(getStorageKey(user.id));
                if (!recentSearches) return;

                const data = JSON.parse(recentSearches);

                const validation = searchResultItemSchema.array().safeParse(data);

                if (validation.success) {
                    state.recentSearches = validation.data;
                } else {
                    console.error("Invalid recent searches data in localStorage", validation.error);
                }
            } catch (error) {
                console.error("Failed to parse recent searches data from localStorage", error);
            }
        });
    },
});

export const { addRecentSearch } = recentSearchSlice.actions;
export const recentSearchReducer = recentSearchSlice.reducer;
